import { create } from 'zustand';
import type { DecorKind, DecorMatchResponseItem } from '@pomoshnik/shared';

/**
 * Session-стор фото-подбора (Экран 2.2.3, PR-W2-FIX замечание 2).
 *
 * **Только память, БЕЗ persist (P-009).** Никакого `persist`-middleware /
 * localStorage / IDB — данные живут лишь в памяти текущей сессии и теряются при
 * reload/закрытии вкладки. На сервер по-прежнему уходят только числа LAB
 * (`{kind, mode, l, a, b}`); ни фото, ни PDF, ни canvas-байты не отправляются и
 * не закрепляются на диске.
 *
 * Зачем стор: шаги фото-экрана — внутренний state компонента. При переходе в
 * карточку декора (`/decors/:kind/:globalId` — отдельный роут) PhotoPicker
 * ремаунтится, локальный state теряется. Стор переживает ремаунт и позволяет
 * «Назад» из карточки вернуться к СПИСКУ результатов, а затем «Назад» из
 * результатов — к выбору режима **с уже загруженным фото** (а не на пустой
 * экран загрузки).
 *
 * Что храним (всё — только в памяти сессии, P-009):
 *   - `imageEl` / `imageUrl` / `imageWidth/Height` — загруженное фото (для показа
 *     и повторного подбора при возврате). objectURL НЕ revoke'ится, пока фото в
 *     сторе; revoke — на реальном сбросе («Загрузить другое фото») через `clear()`
 *     или при замене новым фото в `setImage()`.
 *   - `matchItems` — карточки совпадений (публичные каталожные данные: globalId/
 *     name/brand/deltaE/photoKey, НЕ ПДн).
 *   - `kind` — для валидации rehydrate (результаты относятся к этому kind).
 */
export interface PhotoMatchState {
  imageEl: HTMLImageElement | null;
  imageUrl: string | undefined;
  imageWidth: number;
  imageHeight: number;
  matchItems: readonly DecorMatchResponseItem[] | undefined;
  kind: DecorKind | null;
}

export interface PhotoMatchActions {
  /** Сохраняет загруженное фото (растр/страница PDF). Revoke'ит прежний url. */
  setImage: (session: {
    imageEl: HTMLImageElement;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    kind: DecorKind;
  }) => void;
  /** Сохраняет результаты подбора (фото/kind уже выставлены `setImage`). */
  setResults: (items: readonly DecorMatchResponseItem[]) => void;
  /** Полный сброс сессии + revoke objectURL фото. */
  clear: () => void;
}

export type PhotoMatchStore = PhotoMatchState & PhotoMatchActions;

const INITIAL_STATE: PhotoMatchState = {
  imageEl: null,
  imageUrl: undefined,
  imageWidth: 0,
  imageHeight: 0,
  matchItems: undefined,
  kind: null,
};

// revokeObjectURL на data-URL (PDF) — безвредный no-op; try/catch — для jsdom.
function safeRevoke(url: string | undefined) {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* jsdom / unsupported — no-op */
  }
}

export const usePhotoMatchStore = create<PhotoMatchStore>((set) => ({
  ...INITIAL_STATE,
  setImage: (session) => {
    set((prev) => {
      if (prev.imageUrl && prev.imageUrl !== session.imageUrl) safeRevoke(prev.imageUrl);
      // Новое фото → прежние результаты невалидны до нового подбора.
      return {
        imageEl: session.imageEl,
        imageUrl: session.imageUrl,
        imageWidth: session.imageWidth,
        imageHeight: session.imageHeight,
        kind: session.kind,
        matchItems: undefined,
      };
    });
  },
  setResults: (items) => {
    set({ matchItems: items });
  },
  clear: () => {
    set((prev) => {
      safeRevoke(prev.imageUrl);
      return { ...INITIAL_STATE };
    });
  },
}));

/** Тестовый util: сбрасывает стор в initial (использовать в `beforeEach`). */
export function resetPhotoMatchStoreForTests(): void {
  usePhotoMatchStore.setState({ ...INITIAL_STATE });
}
